class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.eventListeners = new Map(); // Track event listeners to prevent duplicates
    
    // Immediately set up cart icon protection to prevent navigation
    this.setupCartIconProtection();
  }

  setupCartIconProtection() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    // Remove the href to prevent navigation, but keep pointer cursor
    cartLink.removeAttribute('href');
    cartLink.style.cursor = 'pointer';

    // Only add the click handler ONCE — use a property on the element to track it
    if (cartLink._cartDrawerHandler) return;

    const immediateHandler = (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!this.initialized) {
        this.tryInitialize();
      }

      if (this.initialized) {
        if (this.classList.contains('active')) {
          this.close();
        } else {
          this.open(cartLink);
        }
      } else {
        this.pendingOpenAction = () => this.open(cartLink);
      }
    };

    cartLink.addEventListener('click', immediateHandler, { capture: true });
    cartLink._cartDrawerHandler = immediateHandler;
    this.eventListeners.set(cartLink, { immediate: immediateHandler });
  }

  connectedCallback() {
    if (this.initialized) return;
    this.tryInitialize();
    if (!this.initialized && !this.observer) {
      this.observer = new MutationObserver(() => this.tryInitialize());
      this.observer.observe(this, { childList: true, subtree: true });
    }
  }

  tryInitialize() {
    // Only initialize if overlay, cart icon, and close button are present
    const overlay = this.querySelector('#CartDrawer-Overlay');
    const cartLink = document.querySelector('#cart-icon-bubble');
    const closeButton = this.querySelector('.drawer__close');
    
    if (!overlay || !cartLink) {
      return;
    }
    
    // Close button might be inside cart-drawer-items which gets replaced
    // Try to find it in multiple places
    if (!closeButton) {
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      const drawerInner = this.querySelector('.drawer__inner');
      
      // Try to find close button in drawer inner or as direct child
      const potentialCloseButton = drawerInner?.querySelector('.drawer__close') || 
                                    this.querySelector('.drawer__header .drawer__close') ||
                                    this.querySelector('button.drawer__close');
      
      if (!potentialCloseButton) {
        // Set up observer to wait for close button to appear
        if (!this.closeButtonObserver) {
          this.closeButtonObserver = new MutationObserver(() => {
            const foundCloseButton = this.querySelector('.drawer__close');
            if (foundCloseButton && !this.initialized) {
              this.closeButtonObserver.disconnect();
              this.closeButtonObserver = null;
              this.tryInitialize();
            }
          });
          this.closeButtonObserver.observe(this, { childList: true, subtree: true });
        }
        return;
      }
    }
    
    const finalCloseButton = closeButton || this.querySelector('.drawer__close');

    // Keep MutationObserver active to detect if close button is replaced
    if (!this.observer) {
      const observerCallback = (mutations) => {
        let needsReinit = false;
        
        mutations.forEach((mutation) => {
          // Check if nodes were added/removed that might affect close button or overlay
          if (mutation.type === 'childList') {
            // Check if close button or overlay were removed
            const currentCloseButton = this.querySelector('.drawer__close');
            const currentOverlay = this.querySelector('#CartDrawer-Overlay');
            
            if (!currentCloseButton || !currentOverlay) {
              needsReinit = true;
            } else if (this.initialized) {
              // Check if the close button element reference changed (was replaced)
              const closeButtonHasListeners = this.eventListeners && this.eventListeners.has(currentCloseButton);
              if (!closeButtonHasListeners) {
                needsReinit = true;
              }
            }
          }
        });
        
        if (needsReinit) {
          this.initialized = false;
          // Use setTimeout to avoid re-entrancy issues
          setTimeout(() => this.tryInitialize(), 10);
        } else if (!this.initialized) {
          // Initial observer behavior - try to initialize if not already done
          this.tryInitialize();
        }
      };
      
      this.observer = new MutationObserver(observerCallback);
      this.observer.trackReplacement = true;
      this.observer.observe(this, { childList: true, subtree: true });
    }

    // Clean up existing event listeners to prevent duplicates
    this.cleanupEventListeners();

    // Add event listeners with proper tracking
    this.addEventListeners(overlay, cartLink, finalCloseButton);
    
    this.setHeaderCartIconAccessibility();
    this.initialized = true;

    if (!this._hasCheckedCartState) {
      this._hasCheckedCartState = true;
      this.checkAndUpdateCartState();
    }
    
    // Execute any pending open action
    if (this.pendingOpenAction) {
      this.pendingOpenAction();
      this.pendingOpenAction = null;
    }
  }

  cleanupEventListeners() {
    // Remove existing event listeners for internal drawer elements only
    // Cart icon handlers live on the element itself (_cartDrawerHandler) and are never duplicated
    const cartIcon = document.querySelector('#cart-icon-bubble');

    this.eventListeners.forEach((listener, element) => {
      if (!element || !element.removeEventListener || element === cartIcon) return;
      // listener may be a function or an object — handle both
      if (typeof listener === 'function') {
        element.removeEventListener('click', listener);
        element.removeEventListener('keyup', listener);
      }
    });

    // Keep cart icon entry, clear the rest
    const cartIconEntry = this.eventListeners.get(cartIcon);
    this.eventListeners.clear();
    if (cartIconEntry) {
      this.eventListeners.set(cartIcon, cartIconEntry);
    }
  }

  addEventListeners(overlay, cartLink, closeButton) {
    // Escape key handler
    const escapeHandler = (evt) => evt.code === 'Escape' && this.close();
    this.addEventListener('keyup', escapeHandler);
    this.eventListeners.set(this, escapeHandler);

    // Overlay click handler
    const overlayHandler = this.close.bind(this);
    overlay.addEventListener('click', overlayHandler);
    this.eventListeners.set(overlay, overlayHandler);

    // Close button handler
    const closeButtonHandler = this.close.bind(this);
    closeButton.addEventListener('click', closeButtonHandler);
    this.eventListeners.set(closeButton, closeButtonHandler);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    // Only set accessibility attributes — click handling is done by setupCartIconProtection
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    // Add keyboard support (space key) only once
    if (!cartLink._cartDrawerKeydown) {
      const keydownHandler = (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          event.stopPropagation();
          if (this.classList.contains('active')) {
            this.close();
          } else {
            this.open(cartLink);
          }
        }
      };
      cartLink.addEventListener('keydown', keydownHandler);
      cartLink._cartDrawerKeydown = keydownHandler;
    }
  }

  open(triggeredBy) {
    // Ensure drawer is initialized before opening
    if (!this.initialized) {
      this.tryInitialize();
      if (!this.initialized) {
        // Last resort — queue for when initialization completes
        this.pendingOpenAction = () => this.open(triggeredBy);
        return;
      }
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    // Add animation classes
    this.classList.add('animate', 'active');

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  // Public method for external access
  safeOpen(triggeredBy) {
    if (!this.initialized) {
      this.tryInitialize();
      if (!this.initialized) {
        // Queue the open action
        this.pendingOpenAction = () => this.open(triggeredBy);
        return;
      }
    }
    
    if (this.classList.contains('active')) {
      this.close();
    } else {
      this.open(triggeredBy);
    }
  }

  // Emergency fallback method
  emergencyOpen(triggeredBy) {
    // Force initialization if needed
    if (!this.initialized) {
      this.tryInitialize();
    }
    
    // Force open even if not fully initialized
    if (this.classList.contains('active')) {
      this.close();
    } else {
      this.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
      
      // Try to initialize after opening
      setTimeout(() => {
        if (!this.initialized) {
          this.tryInitialize();
        }
      }, 100);
    }
  }

  close() {
    this.classList.remove('active');

    // Remove any forced inline styles
    this.style.removeProperty('visibility');
    this.style.removeProperty('display');
    this.style.removeProperty('opacity');
    this.style.removeProperty('pointer-events');

    // Remove focus trap
    if (this.activeElement) {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const sections = parsedState.sections;
    if (!sections) {
      return;
    }

    if (this.observer) this.observer.disconnect();

    Object.keys(sections).forEach(sectionId => {
      const sectionContent = sections[sectionId];
      if (!sectionContent) return;

      const html = new DOMParser().parseFromString(sectionContent, 'text/html');

      if (sectionId === 'cart-drawer') {
        // Update cart items
        const sourceItems = html.querySelector('cart-drawer-items');
        const targetItems = this.querySelector('cart-drawer-items');
        if (sourceItems && targetItems) {
          targetItems.innerHTML = sourceItems.innerHTML;
        }

        // Update header (contains "My Cart (X)" count)
        const sourceHeader = html.querySelector('.drawer__header');
        const targetHeader = this.querySelector('.drawer__header');
        if (sourceHeader && targetHeader) {
          targetHeader.innerHTML = sourceHeader.innerHTML;
        }

        // Update progress bar (free shipping bar). This swaps in a fresh fill bar
        // already at its target width, so animate it from the previously-shown
        // width via the cart-drawer-items progressBar() helper (runs synchronously
        // before paint to avoid a snap/flash), matching the qty-change path.
        const sourceProgress = html.querySelector('.free-shipping-box');
        const targetProgress = this.querySelector('.free-shipping-box');
        if (sourceProgress && targetProgress) {
          targetProgress.innerHTML = sourceProgress.innerHTML;
          const drawerItems = this.querySelector('cart-drawer-items');
          if (drawerItems && typeof drawerItems.progressBar === 'function') {
            drawerItems.progressBar();
          }
        }

        // Update footer (subtotal, checkout, etc.)
        const sourceFooter = html.querySelector('.drawer__footer');
        const targetFooter = this.querySelector('.drawer__footer');
        if (sourceFooter && targetFooter) {
          targetFooter.innerHTML = sourceFooter.innerHTML;
        }

        this.classList.remove('is-empty');
        if (targetItems) targetItems.classList.remove('is-empty');

      } else if (sectionId === 'cart-icon-bubble') {
        const targetIcon = document.querySelector('#cart-icon-bubble');
        const iconSource = html.querySelector('.shopify-section') || html.querySelector('[id="cart-icon-bubble"]') || html.body;
        if (targetIcon && iconSource) {
          targetIcon.innerHTML = iconSource.innerHTML;
          this.setupCartIconProtection();
        }
      }
    });

    if (this.observer) {
      requestAnimationFrame(() => {
        this.observer.observe(this, { childList: true, subtree: true });
      });
    }

    // Open the drawer after a brief delay to allow DOM to settle
    setTimeout(() => {
      this.open();
      // Re-bind shipping protection after drawer re-renders
      if (typeof window.bindShippingProtection === 'function') {
        window.bindShippingProtection();
      }
      if (typeof window.autoAddShippingProtection === 'function') {
        window.autoAddShippingProtection();
      }
    }, 50);
  }

  initializeCartItems() {
    // Force re-initialization of cart-drawer-items
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    
    if (cartDrawerItems) {
      // Force the custom element to re-initialize
      if (cartDrawerItems.connectedCallback) {
        cartDrawerItems.connectedCallback();
      }
      
      // Also try to initialize if the method exists
      if (typeof cartDrawerItems.initialize === 'function') {
        cartDrawerItems.initialize();
      }
      
      // Force a re-render by triggering a custom event
      cartDrawerItems.dispatchEvent(new CustomEvent('cart-update', { detail: { source: 'cart-drawer' } }));
    }
    
    // Also trigger any custom element upgrades
    if (window.customElements && window.customElements.upgrade) {
      const customElements = document.querySelectorAll('cart-drawer-items, quantity-popover, cart-remove-button');
      
      customElements.forEach(element => {
        if (element.tagName && element.tagName.includes('-')) {
          window.customElements.upgrade(element);
        }
      });
    }
    
    // Check if cart items are visible after initialization
    setTimeout(() => {
      const cartItems = document.querySelectorAll('.cart-item');
      if (cartItems.length > 0) {
        // Force the form to be visible
        const cartForm = document.querySelector('#CartDrawer-Form');
        if (cartForm) {
          cartForm.style.display = 'block';
          cartForm.style.visibility = 'visible';
        }
      }
    }, 50);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    try {
      // Check if html is null or empty
      if (!html) {
        return '';
      }
      
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      
      let element = parsed.querySelector(selector);
      if (element) {
        // For cart-drawer section, look for the drawer__inner content
        if (selector === '.drawer__inner') {
          const drawerInner = element.querySelector('.drawer__inner');
          if (drawerInner) {
            element = drawerInner;
          }
        }
        
        return element.innerHTML;
      } else {
        // Try alternative selectors based on the target selector
        let alternativeSelectors;
        if (selector === '.drawer__inner') {
          alternativeSelectors = ['#CartDrawer', 'cart-drawer', 'cart-drawer-items', '.drawer__inner'];
        } else {
          alternativeSelectors = ['.shopify-section', '#cart-icon-bubble'];
        }
        
        for (const altSelector of alternativeSelectors) {
          const altElement = parsed.querySelector(altSelector);
          if (altElement) {
            // For cart-drawer, try to find the inner content
            if (altSelector === '#CartDrawer' || altSelector === 'cart-drawer') {
              const drawerInner = altElement.querySelector('.drawer__inner');
              if (drawerInner) {
                return drawerInner.innerHTML;
              }
            }
            
            return altElement.innerHTML;
          }
        }
        
        // If no specific selector works, return the body content
        return parsed.body.innerHTML;
      }
    } catch (error) {
      return '';
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',  // Changed from 'CartDrawer' to match the actual section filename
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  checkAndUpdateCartState() {
    // Prevent multiple simultaneous calls
    if (this._checkingCartState) {
      return;
    }
    this._checkingCartState = true;
    
    // Add a small delay to ensure any pending cart updates are complete
    setTimeout(() => {
      // Fetch current cart state
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          if (cart.item_count > 0) {
            // Cart has items, update drawer to show non-empty state
            this.updateToNonEmptyState();
          } else {
            // Cart is empty, ensure drawer shows empty state
            this.updateToEmptyState();
          }
        })
        .catch(error => {
          // Error handled silently
        })
        .finally(() => {
          // Reset flag after a delay
          setTimeout(() => {
            this._checkingCartState = false;
          }, 500);
        });
    }, 100); // 100ms delay to ensure cart updates are complete
  }

  updateToNonEmptyState() {
    this.classList.remove('is-empty');

    const emptyContent = this.querySelector('.drawer__inner-empty');
    if (emptyContent) {
      emptyContent.style.display = 'none';
    }

    const cartDrawerItems = this.querySelector('cart-drawer-items');
    if (cartDrawerItems) {
      cartDrawerItems.classList.remove('is-empty');
    }

    const cartForm = this.querySelector('#CartDrawer-Form');
    if (cartForm) {
      cartForm.style.display = '';
      cartForm.style.visibility = '';
    }
  }

  updateToEmptyState() {
    this.classList.add('is-empty');

    const emptyContent = this.querySelector('.drawer__inner-empty');
    if (emptyContent) {
      emptyContent.style.display = '';
    }

    const cartDrawerItems = this.querySelector('cart-drawer-items');
    if (cartDrawerItems) {
      cartDrawerItems.classList.add('is-empty');
    }
  }

  // Render height-matched shimmer skeleton line-items into the drawer while
  // the fresh cart HTML is being fetched. Returns the outgoing markup so the
  // caller can restore it if the fetch fails.
  renderCartSkeleton(itemsEl) {
    if (!itemsEl) return null;
    const snapshot = itemsEl.innerHTML;
    const rows = Math.min(Math.max(itemsEl.querySelectorAll('.cart-item').length, 1), 6);
    const minHeight = itemsEl.offsetHeight;
    const line =
      '<div class="cart-skeleton__item">' +
      '<div class="skeleton cart-skeleton__media"></div>' +
      '<div class="cart-skeleton__lines">' +
      '<div class="skeleton cart-skeleton__line"></div>' +
      '<div class="skeleton cart-skeleton__line cart-skeleton__line--short"></div>' +
      '</div></div>';
    let markup = '';
    for (let i = 0; i < rows; i++) markup += line;
    itemsEl.innerHTML =
      '<div class="cart-skeleton" aria-hidden="true"' +
      (minHeight ? ' style="min-height:' + minHeight + 'px"' : '') +
      '>' + markup + '</div>';
    return snapshot;
  }

  fetchAndUpdateCartContent() {
    if (this._fetchingCartContent) return;
    this._fetchingCartContent = true;

    // Only show skeletons when the drawer is actually open and has items to
    // refresh — background refreshes on a closed drawer stay invisible.
    const itemsEl = this.querySelector('cart-drawer-items');
    let cartSnapshot = null;
    if (itemsEl && this.classList.contains('active') && !this.classList.contains('is-empty')) {
      cartSnapshot = this.renderCartSkeleton(itemsEl);
    }

    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const cartDrawerItems = html.querySelector('cart-drawer-items');
        const cartDrawerFooter = html.querySelector('.cart-drawer__footer');

        // Pause the MutationObserver while we replace DOM to avoid
        // triggering a reinit → checkAndUpdateCartState loop.
        if (this.observer) this.observer.disconnect();

        if (cartDrawerItems) {
          const target = this.querySelector('cart-drawer-items');
          if (target) {
            target.innerHTML = cartDrawerItems.innerHTML;
            if (target.tryInitialize) target.tryInitialize();
          }
        }

        if (cartDrawerFooter) {
          const target = this.querySelector('.cart-drawer__footer');
          if (target) target.replaceWith(cartDrawerFooter);
        }

        // Re-observe after DOM is settled
        if (this.observer) {
          requestAnimationFrame(() => {
            this.observer.observe(this, { childList: true, subtree: true });
          });
        }
      })
      .catch(() => {
        // Restore the real line-items so skeletons never get stuck on a
        // failed refresh.
        if (cartSnapshot != null) {
          const target = this.querySelector('cart-drawer-items');
          if (target) target.innerHTML = cartSnapshot;
        }
      })
      .finally(() => {
        setTimeout(() => { this._fetchingCartContent = false; }, 1000);
      });
  }

  // Public method to force update cart drawer state
  forceUpdateState() {
    this.checkAndUpdateCartState();
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Cart Drawer upsell variant ID change on select
document.addEventListener('change', function(event) {
  if (event.target && event.target.matches('.upsell__select')) {
    const selector = event.target;
    const selectedOption = selector.options[selector.selectedIndex];
    const newVariantId = selectedOption.value;

    const formId = selector.getAttribute('form');
    const hiddenInput = document.querySelector('#' + formId + ' input[name="id"]');
    
    if (hiddenInput) {
      hiddenInput.value = newVariantId;
    }
  }
});

